import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { AlertCircle } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useTranslation } from 'react-i18next';

export default function LoginPage() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  // MFA step
  const [mfaToken, setMfaToken] = useState(null);
  const [mfaCode, setMfaCode] = useState('');
  const { login, completeMfaLogin } = useAuth();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const toggleLang = () => { const next = i18n.language === 'pl' ? 'en' : 'pl'; i18n.changeLanguage(next); localStorage.setItem('swibi_language', next); };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const res = await login(email, password);
      if (res?.mfaRequired) {
        setMfaToken(res.mfaToken);
      } else {
        navigate('/');
      }
    } catch (err) {
      setError(err.response?.data?.error || t('auth.loginError'));
    } finally {
      setLoading(false);
    }
  };

  const handleMfaSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await completeMfaLogin(mfaToken, mfaCode.trim());
      navigate('/');
    } catch (err) {
      setError(err.response?.data?.error || t('auth.loginError'));
    } finally {
      setLoading(false);
    }
  };

  const cancelMfa = () => { setMfaToken(null); setMfaCode(''); setError(''); };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-600 via-blue-700 to-indigo-800 flex items-center justify-center p-4 relative overflow-hidden">
      {/* Background decoration */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute -top-40 -left-40 w-80 h-80 bg-white/5 rounded-full" />
        <div className="absolute -bottom-40 -right-40 w-96 h-96 bg-white/5 rounded-full" />
        <div className="absolute top-1/4 right-1/4 w-64 h-64 bg-blue-400/10 rounded-full blur-2xl" />
      </div>

      <div className="absolute top-4 right-4 z-20">
        <button onClick={toggleLang} className="text-sm text-blue-200 hover:text-white hover:bg-white/10 px-3 py-1.5 rounded-lg transition">
          {i18n.language === 'pl' ? 'English' : 'Polski'}
        </button>
      </div>
      <div className="relative z-10 w-full max-w-md">
        {/* Card */}
        <div className="bg-white rounded-2xl shadow-2xl overflow-hidden">
          {/* Logo area */}
          <div className="px-8 pt-8 pb-4 text-center border-b border-gray-100">
            <Link to="/">
              <img src="/logo_full.png" alt="SWIBI" className="h-10 mx-auto" />
            </Link>
          </div>

          {/* Form */}
          <div className="p-8">
            <h1 className="text-lg font-bold text-gray-800 text-center mb-1">
              {mfaToken ? t('auth.mfaTitle') : t('auth.loginTitle')}
            </h1>
            <p className="text-gray-400 text-sm text-center mb-6">
              {mfaToken ? t('auth.mfaSubtitle') : t('auth.loginSubtitle')}
            </p>

            {error && (
              <div className="flex items-center gap-2 p-3 mb-4 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
                <AlertCircle size={16} className="flex-shrink-0" />
                {error}
              </div>
            )}

            {!mfaToken ? (
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{t('auth.emailLabel')}</label>
                  <input type="email" value={email} onChange={e => setEmail(e.target.value)}
                    className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder={t('auth.emailPlaceholder')} required autoFocus />
                </div>
                <div>
                  <div className="flex items-center justify-between mb-1">
                    <label className="block text-sm font-medium text-gray-700">{t('auth.passwordLabel')}</label>
                    <Link to="/forgot-password" className="text-xs text-blue-600 hover:text-blue-700 hover:underline">
                      {t('auth.forgotPassword')}
                    </Link>
                  </div>
                  <input type="password" value={password} onChange={e => setPassword(e.target.value)}
                    className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="••••••••" required />
                </div>
                <button type="submit" disabled={loading}
                  className="w-full py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-semibold disabled:opacity-50 transition shadow-lg shadow-blue-600/25">
                  {loading ? t('auth.loggingIn') : t('auth.loginButton')}
                </button>
              </form>
            ) : (
              <form onSubmit={handleMfaSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{t('auth.mfaCodeLabel')}</label>
                  <input type="text" inputMode="numeric" autoComplete="one-time-code" value={mfaCode}
                    onChange={e => setMfaCode(e.target.value.replace(/[^0-9]/g, '').slice(0, 6))}
                    className="w-full px-4 py-2.5 border border-gray-300 rounded-lg text-center tracking-[0.5em] text-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="000000" required autoFocus />
                </div>
                <button type="submit" disabled={loading || mfaCode.length < 6}
                  className="w-full py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-semibold disabled:opacity-50 transition shadow-lg shadow-blue-600/25">
                  {loading ? t('auth.loggingIn') : t('auth.mfaVerifyButton')}
                </button>
                <button type="button" onClick={cancelMfa}
                  className="w-full text-sm text-gray-500 hover:text-gray-700">
                  {t('auth.mfaBackToLogin')}
                </button>
              </form>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="text-center mt-6">
          <Link to="/" className="text-blue-200 hover:text-white text-sm transition">
            {t('auth.backToHome')}
          </Link>
        </div>
      </div>
    </div>
  );
}
